import React, { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
type TestimonialEntry = {
  quote: string;
  name: string;
  role: string;
  imageUrl: string;
};
const testimonials: TestimonialEntry[] = [{
  quote: 'We never sell 100% of our product, so finding a solution to control our overstock distribution is really valuable. I also think our customers love that we’re now able to take our products back and that they will see it going to another family.',
  name: 'Jacob Faull',
  role: 'Founder, Nature Baby',
  imageUrl: 'https://cdn.magicpatterns.com/uploads/93nsgF443SvG3Z5NEoTyN9/jacob.png'
}, {
  quote: 'Working with ManyCo. has provided Eddy with a beautiful outlet for our deadstock pieces that we otherwise would be unable to sell. We can seamlessly market excess inventory to a different client base, helping us unlock additional revenue.',
  name: 'Meghan Eddings-Feely',
  role: 'Founder, Eddy',
  imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/c6c8a87a-853d-446c-898b-e4de99b47da7.jpg'
}, {
  quote: 'We have had a great experience working with ManyCo. The setup was very smooth. Sales started almost immediately and it is certainly a very useful platform for us to clear surplus stock and samples in the USA.',
  name: 'Hannah Beaumont',
  role: 'Founder, Beaumont Organics',
  imageUrl: 'https://cdn.magicpatterns.com/patterns/generated-images/dcac5bf2-fa65-478f-86ca-39b6396d227a.jpg'
}];
export function Testimonial() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const shouldReduceMotion = useReducedMotion();
  const testimonial = testimonials[activeIndex];
  const selectTestimonial = (nextIndex: number) => {
    setDirection(nextIndex > activeIndex ? 1 : -1);
    setActiveIndex(nextIndex);
  };
  const advance = (step: number) => {
    const nextIndex = (activeIndex + step + testimonials.length) % testimonials.length;
    setDirection(step);
    setActiveIndex(nextIndex);
  };
  return <section className="mx-auto max-w-4xl px-5 pb-8 sm:px-8" aria-labelledby="testimonial-heading" data-id="element-1879" data-mp="xoz0ygua4yb7">
      <motion.figure initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true,
      margin: '-80px'
    }} transition={{
      duration: 0.6
    }} className="overflow-hidden rounded-3xl border border-ink/10 bg-paper" data-id="element-1880" data-mp="12g8q7gbx1zmk">
        <div className="relative min-h-[365px] p-8 text-center sm:min-h-[350px] sm:p-12" data-id="element-1881" data-mp="eqmvb52ku6kd">
          <motion.div key={testimonial.name} initial={shouldReduceMotion ? {
          opacity: 0
        } : {
          opacity: 0,
          x: direction * 28
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.28,
          ease: [0.22, 1, 0.36, 1]
        }} drag={shouldReduceMotion ? false : 'x'} dragConstraints={{
          left: 0,
          right: 0
        }} dragElastic={0.12} onDragEnd={(_, info) => {
          if (Math.abs(info.offset.x) > 55) advance(info.offset.x < 0 ? 1 : -1);
        }} className="cursor-grab active:cursor-grabbing" data-id="element-1882" data-mp="isripm2fm6yk">
            <blockquote id="testimonial-heading" className="font-display text-2xl font-medium leading-snug tracking-tight text-ink sm:text-3xl" data-id="element-1883" data-mp="1zkd7y9uh9ryd">
              &ldquo;{testimonial.quote}&rdquo;
            </blockquote>
            <figcaption className="mt-8 flex items-center justify-center gap-3" data-id="element-1884" data-mp="1qkgn6jb7ld8l">
              <img src={testimonial.imageUrl} alt={testimonial.name} className="h-11 w-11 rounded-full object-cover" data-id="element-1885" data-mp="1lkklwrtwbzwq" />
              <div className="text-left" data-id="element-1886" data-mp="1gn2a8tohv6l1">
                <p className="text-sm font-semibold text-ink" data-id="element-1887" data-mp="1go3nn4szg55x">{testimonial.name}</p>
                <p className="text-sm text-ink/55" data-id="element-1888" data-mp="1cm8l5wzbflqk">{testimonial.role}</p>
              </div>
            </figcaption>
          </motion.div>
        </div>

        <div className="flex items-center justify-between border-t border-ink/10 px-5 py-4 sm:px-8" data-id="element-1889" data-mp="36xx5keobggic">
          <div className="flex gap-2" aria-label="Testimonial navigation" data-id="element-1890" data-mp="3e153f5rveaod">
            {testimonials.map((item, index) => <button key={item.name} type="button" onClick={() => selectTestimonial(index)} aria-label={`Show testimonial from ${item.name}`} aria-current={index === activeIndex ? 'true' : undefined} className={`h-2 rounded-full transition-[width,background-color] duration-200 ${index === activeIndex ? 'w-6 bg-[#BB5E40]' : 'w-2 bg-ink/15 hover:bg-ink/30'}`} data-id="element-1891" data-mp="3izeylgdlhb9z" data-mpi={index} />)}
          </div>
          <div className="flex gap-1" data-id="element-1892" data-mp="2lealogmftpel">
            <button type="button" onClick={() => advance(-1)} className="rounded-lg p-2 text-ink/55 transition-colors hover:bg-cream hover:text-ink" aria-label="Previous testimonial" data-id="element-1893" data-mp="2lb4vey22ohha">
              <ChevronLeftIcon className="h-4 w-4" data-id="element-1894" data-mp="2cedzclnit2mp" />
            </button>
            <button type="button" onClick={() => advance(1)} className="rounded-lg p-2 text-ink/55 transition-colors hover:bg-cream hover:text-ink" aria-label="Next testimonial" data-id="element-1895" data-mp="2ha2umcsibxd7">
              <ChevronRightIcon className="h-4 w-4" data-id="element-1896" data-mp="1srj6mz7couw4" />
            </button>
          </div>
        </div>
      </motion.figure>
    </section>;
}